import React, { useCallback, useLayoutEffect, useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { AssetPrompt, ChatItem, DataDictionaryToggle } from '../../molecules';
import { useAgenticAssetBuilder } from '../../../providers';
import { questionShape } from '../../../../../propsShape';
import { Text, Icon, BoldText } from '../../../../../components';

import { DataDictionaryModal } from '../DataDictionaryModal';
import { usePortal } from '../../../../../providers';

const AssetChatContainer = styled.div`
  display: flex;
  flex-direction: column;
  overflow-y: scroll;
  height: 100%;
  padding: 16px;
  margin: 0 auto;
  max-width: 700px;
  width: 100%;
  box-sizing: border-box;
  scrollbar-color: var(--gray-20) transparent;
  scrollbar-width: thin;
`;

const StyledAssetPrompt = styled(AssetPrompt)`
  width: 100%;
  max-width: 700px;
  margin: 0 auto;
`;

const ChatItemContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  opacity: ${({ $opacity }) => ($opacity ? 0.5 : 1)};
  opacity: ${({ $isBranchQuestion }) => $isBranchQuestion && 1};
`;

const AssetPromptContainer = styled.div`
  display: flex;
  align-items: flex-start;
  flex-direction: column;
  padding: 18px 16px;
`;

const BranchQuestionInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
  background-color: var(--wash-orange-default);
  width: 100%;
  box-sizing: border-box;
  border-radius: 10px;
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
  padding: 12px 12px 24px 12px;
  margin: 0 auto;
  margin-bottom: -12px;
  max-width: 700px;
`;

const CancelButton = styled.button.attrs({ type: 'button' })`
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background-color: transparent;
  color: var(--content-subtitle);
  width: 20px;
  height: 20px;
  margin-left: auto;
  align-self: flex-start;

  &:hover {
    color: var(--action-tertiary-hover);
    cursor: pointer;
  }
`;

const AgenticAssetChat = ({
  setIsAssetCollapsed,
  switchToChartMode,
  handleViewChange,
  setActiveChildQuestion,
  activeChildQuestion,
  assetQuestion = null,
  isReadOnly = false
}) => {
  const {
    isChartMode,
    currentUser,
    currentOrganization,
    columnMentionItems,
    metrics,
    databaseDescription,
    questions,
    handleCreateQuestion,
    handleSelectQuestion,
    handleDeleteQuestion,
    handleRetryQuestion,
    selectionPath,
    selectedQuestion,
    sources,
    termTranslations,
    users,
    findEntityMatches,
    matchingEntities,
    handleSelectEntity
  } = useAgenticAssetBuilder();

  const [branchQuestion, setBranchQuestion] = useState(null);

  const { showPortal } = usePortal();

  const editorRef = useRef(null);
  const chatRef = useRef(null);
  const isInitialRender = useRef(true);
  const insertMentionRef = useRef(null);
  const selectionPathRef = useRef(selectionPath);
  const branchQuestionRef = useRef(null);

  useEffect(() => {
    branchQuestionRef.current = branchQuestion;
  }, [branchQuestion]);

  // Keep selectionPathRef updated with the latest value
  useEffect(() => {
    selectionPathRef.current = selectionPath;
  }, [selectionPath]);

  useLayoutEffect(() => {
    if (chatRef.current) {
      chatRef.current.scrollTo({
        top: chatRef.current.scrollHeight,
        behavior: isInitialRender.current ? 'auto' : 'smooth'
      });
    }
    isInitialRender.current = false;
  }, [chatRef.current?.scrollHeight]);

  const onOpenDataDictionaryModal = useCallback(() => {
    showPortal(
      <DataDictionaryModal
        databaseDescription={databaseDescription}
        insertMention={insertMentionRef.current}
        sources={sources}
        termTranslations={termTranslations}
        isReadOnly={!currentUser?.canAsk}
      />
    );
  }, [
    showPortal,
    databaseDescription,
    editorRef.current,
    insertMentionRef.current,
    sources,
    termTranslations,
    currentUser?.canAsk
  ]);

  const handleCreateQuestionFromPrompt = useCallback(
    (body, forceChartMode = false) => {
      setActiveChildQuestion(null);
      // For agentic chats, we simplify the branch logic
      const questionOnWhichFollowUpIsBeingCreated = branchQuestionRef.current?.id || null;
      let command = null;

      if (isChartMode || forceChartMode) {
        command = questionOnWhichFollowUpIsBeingCreated?.isChart ? 'chart-refine' : 'chart';
      }

      setBranchQuestion(null);
      // scroll to the bottom of the chat
      return handleCreateQuestion(body, questionOnWhichFollowUpIsBeingCreated, command);
    },
    [isChartMode, handleCreateQuestion, setActiveChildQuestion]
  );

  const handleRetryClick = useCallback(
    (question, evt) => {
      setActiveChildQuestion(null);
      evt.stopPropagation();
      handleRetryQuestion(question);
    },
    [handleRetryQuestion, setActiveChildQuestion]
  );

  const handleUndoClick = useCallback(
    (questionId, evt) => {
      setActiveChildQuestion(null);
      evt.stopPropagation();
      handleDeleteQuestion(questionId);
    },
    [handleDeleteQuestion, setActiveChildQuestion]
  );

  const handleBranchClick = useCallback(
    (question) => {
      setActiveChildQuestion(null);
      setBranchQuestion(question);
      editorRef.current?.focus();
    },
    [editorRef, setBranchQuestion, setActiveChildQuestion]
  );

  const onQuestionSelect = useCallback(
    (question, view = 'table') => {
      setActiveChildQuestion(null);
      handleSelectQuestion(question);
      handleViewChange(view);
      if (view === 'table') {
        setIsAssetCollapsed(false);
      }
    },
    [handleSelectQuestion, handleViewChange, setIsAssetCollapsed, setActiveChildQuestion]
  );

  // For agentic chats, we simplify the question rendering since we don't have the complex follow-up structure
  const renderQuestion = useCallback(
    (question) => {
      if (!question) return null;

      const dataDescription = question.answer?.dataDescription || 'Processing answer...';

      return (
        <ChatItemContainer key={question.id}>
          <ChatItem
            key={question.id}
            question={question}
            sources={sources}
            users={users}
            switchToChartMode={switchToChartMode}
            isSaved={false} // Simplified for agentic chats
            isSqlChanged={false} // Simplified for agentic chats
            currentUser={currentUser}
            isSelected={selectedQuestion?.id === question.id}
            onUndoClick={(evt) => handleUndoClick(question.id, evt)}
            onOpenDataDictionaryModal={onOpenDataDictionaryModal}
            handleCreateQuestionFromPrompt={handleCreateQuestionFromPrompt}
            handleViewChange={handleViewChange}
            onQuestionSelect={(view) => onQuestionSelect(question, view)}
            onRetryQuestion={(evt) => handleRetryClick(question, evt)}
            isLastQuestion // Simplified for agentic chats
            setBranchQuestion={handleBranchClick}
            dataDescription={dataDescription}
            isReadOnly={isReadOnly}
            branchQuestion={branchQuestion}
            setActiveChildQuestion={setActiveChildQuestion}
            activeChildQuestion={activeChildQuestion}
            isAgenticChat
          />
        </ChatItemContainer>
      );
    },
    [
      users,
      currentUser,
      selectedQuestion,
      handleDeleteQuestion,
      onQuestionSelect,
      handleCreateQuestionFromPrompt,
      handleViewChange,
      onOpenDataDictionaryModal,
      handleRetryClick,
      handleUndoClick,
      handleBranchClick,
      branchQuestion,
      sources,
      activeChildQuestion,
      setActiveChildQuestion,
      switchToChartMode,
      isReadOnly
    ]
  );

  return (
    <>
      <AssetChatContainer ref={chatRef}>
        {assetQuestion && (
          <ChatItem
            key={assetQuestion.id}
            question={assetQuestion}
            isSelected={selectedQuestion?.id === assetQuestion.id || !selectedQuestion}
            currentOrganizationName={currentOrganization?.displayName}
            onQuestionSelect={() => onQuestionSelect(assetQuestion)}
            isSavedAssetQuestion
            sources={sources}
            users={users}
          />
        )}
        {questions.map((q) => renderQuestion(q))}
      </AssetChatContainer>
      <AssetPromptContainer>
        {branchQuestion && (
          <BranchQuestionInfo>
            <Text>
              <BoldText
                text={`Following up on ${branchQuestion.assetTitle || branchQuestion.body}`}
                boldPart={branchQuestion.assetTitle || branchQuestion.body}
              />
            </Text>
            <CancelButton onClick={() => setBranchQuestion(null)}>
              <Icon name='close' displaySize={20} />
            </CancelButton>
          </BranchQuestionInfo>
        )}
        <StyledAssetPrompt
          ref={editorRef}
          insertMentionRef={insertMentionRef}
          disabled={isReadOnly}
          handleCreateQuestion={handleCreateQuestionFromPrompt}
          placeholder='Ask a question…'
          matchingEntities={matchingEntities}
          mentionItems={{
            columns: columnMentionItems,
            metrics
          }}
          termTranslations={termTranslations}
          findEntityMatches={findEntityMatches}
          handleSelectEntity={handleSelectEntity}
          sources={sources}
          {...(databaseDescription &&
            sources?.length > 0 &&
            termTranslations && {
              actions: (
                <DataDictionaryToggle onClick={onOpenDataDictionaryModal} label='Data Dictionary' />
              )
            })}
        />
      </AssetPromptContainer>
    </>
  );
};

AgenticAssetChat.propTypes = {
  currentQuestion: questionShape,
  assetQuestion: questionShape,
  isReadOnly: PropTypes.bool,
  setIsAssetCollapsed: PropTypes.func.isRequired,
  switchToChartMode: PropTypes.func.isRequired,
  handleViewChange: PropTypes.func.isRequired,
  setActiveChildQuestion: PropTypes.func.isRequired,
  activeChildQuestion: PropTypes.number
};

export { AgenticAssetChat };
